<template>
  <div class="limit-progress-bar" :class="{ disabled: disabled }">
    <div class="flex items-center justify-between mb-1.5">
      <div class="flex items-center gap-1">
        <span class="text-sm font-semibold text-zinc-700 dark:text-zinc-300">{{ label }}</span>
        <span v-if="disabledNote" class="text-xs text-zinc-500 dark:text-zinc-400">{{ disabledNote }}</span>
      </div>
      <span class="text-sm font-semibold text-zinc-700 dark:text-zinc-300">{{ current }}/{{ max }}</span>
    </div>
    <div class="progress-track">
      <div class="progress-fill" :style="{ width: `${percentage}%` }" :class="getProgressClass()" />
    </div>
    <p v-if="note" class="text-xs text-zinc-500 dark:text-zinc-400 text-right mt-1">{{ note }}</p>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

interface Props {
  label: string;
  current: number;
  max: number;
  disabled?: boolean;
  disabledNote?: string;
  note?: string;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
});

const percentage = computed(() => {
  if (props.max === 0) return 0;
  return Math.min((props.current / props.max) * 100, 100);
});

const getProgressClass = () => {
  const percent = percentage.value;

  if (percent >= 90) return 'critical';
  if (percent >= 70) return 'warning';
  return 'normal';
};
</script>

<style scoped>
.limit-progress-bar {
  @apply w-full;
}

/* Disabled state - gray out text and progress bar */
.limit-progress-bar.disabled {
  @apply opacity-50;
}

.limit-progress-bar.disabled span {
  @apply text-zinc-400 dark:text-zinc-500;
}

.progress-track {
  @apply w-full h-1 rounded-full bg-zinc-100 dark:bg-zinc-700 overflow-hidden;
}

.progress-fill {
  @apply h-full rounded-full transition-all duration-300 ease-in-out;
}

.progress-fill.normal {
  @apply bg-orange-500 dark:bg-orange-400;
}

.progress-fill.warning {
  @apply bg-orange-500 dark:bg-orange-400;
}

.progress-fill.critical {
  @apply bg-red-500 dark:bg-red-400;
}

/* Disabled progress bar */
.limit-progress-bar.disabled .progress-fill {
  @apply bg-zinc-300 dark:bg-zinc-600;
}
</style>
